import { useI18n } from '@ecom/i18n';
import type { ReactNode } from 'react';

import { cx } from '../utils/cx';

export interface PaginationProps {
  /** 1-based. */
  readonly page: number;
  readonly pageCount: number;
  /** How many page numbers to show around the current one. */
  readonly siblingCount?: number;
  readonly onPageChange: (page: number) => void;
}

type PageItem = number | 'gap-start' | 'gap-end';

/**
 * Builds the page list with at most one gap on each side.
 *
 * Kept as a pure exported function so the truncation rules can be tested
 * directly on their edge cases (first page, last page, too few pages to
 * truncate at all) without rendering and querying the DOM for every case.
 */
export function paginationItems(
  page: number,
  pageCount: number,
  siblingCount = 1,
): readonly PageItem[] {
  // first + last + current + one gap per side + siblings on each side
  const maxSlots = siblingCount * 2 + 5;

  if (pageCount <= maxSlots) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const start = Math.max(2, page - siblingCount);
  const end = Math.min(pageCount - 1, page + siblingCount);
  const items: PageItem[] = [1];

  if (start > 2) items.push('gap-start');
  for (let candidate = start; candidate <= end; candidate += 1) items.push(candidate);
  if (end < pageCount - 1) items.push('gap-end');

  items.push(pageCount);
  return items;
}

/**
 * Pagination (§7.1 primitives).
 *
 * No pagination control exists in the three prototypes — they render fixed
 * demo lists — so nothing was copied. Rather than invent a new visual
 * language, this reuses the prototypes' own `.filter-chip` vocabulary
 * (bordered pill, `--ecom-accent` fill when active), which is how they mark
 * a selected item elsewhere. Flagged here so the choice is visible at review
 * rather than assumed to be extracted like the rest.
 *
 * The current page carries `aria-current="page"` and stays rendered as a
 * button so keyboard order is uninterrupted; direction is handled by the
 * flex row itself, so previous/next sit on the correct edges in RTL with no
 * component-level branching.
 */
export function Pagination({
  page,
  pageCount,
  siblingCount = 1,
  onPageChange,
}: PaginationProps): ReactNode {
  const { t } = useI18n();

  if (pageCount <= 1) return null;

  const items = paginationItems(page, pageCount, siblingCount);
  const isFirst = page <= 1;
  const isLast = page >= pageCount;

  return (
    <nav className="ecom-pagination" aria-label={t('pagination.label')}>
      <button
        type="button"
        className="ecom-pagination__arrow"
        aria-label={t('pagination.previous')}
        disabled={isFirst}
        onClick={() => {
          onPageChange(page - 1);
        }}
      >
        <ArrowIcon direction="previous" />
      </button>

      <ol className="ecom-pagination__list">
        {items.map((item) =>
          typeof item === 'number' ? (
            <li key={item}>
              <button
                type="button"
                className={cx('ecom-pagination__page', item === page && 'is-active')}
                aria-label={`${t('pagination.page')} ${String(item)}`}
                {...(item === page ? { 'aria-current': 'page' } : {})}
                onClick={() => {
                  onPageChange(item);
                }}
              >
                {item}
              </button>
            </li>
          ) : (
            // Presentational only: the gap is a visual shorthand, and
            // announcing "ellipsis" adds nothing a screen-reader user can act
            // on — the reachable page numbers around it already convey it.
            <li key={item} className="ecom-pagination__gap" aria-hidden="true">
              …
            </li>
          ),
        )}
      </ol>

      <button
        type="button"
        className="ecom-pagination__arrow"
        aria-label={t('pagination.next')}
        disabled={isLast}
        onClick={() => {
          onPageChange(page + 1);
        }}
      >
        <ArrowIcon direction="next" />
      </button>
    </nav>
  );
}

/**
 * The chevron is the one thing that cannot flip by itself: it is drawn
 * geometry, not text or layout. `scale(-1, 1)` under `dir="rtl"` is applied
 * in CSS via `[dir='rtl']`, so this component stays direction-agnostic.
 */
function ArrowIcon({ direction }: { readonly direction: 'previous' | 'next' }): ReactNode {
  return (
    <svg
      className="ecom-pagination__icon"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={direction === 'previous' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
